import React, { useEffect, useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

const IletisimPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    // SEO Meta Tags
    document.title = 'İletişim - Selçuklu Konya İş Sağlığı Güvenliği Özgür OSGB';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Konya Selçuklu Özgür OSGB ile iletişime geçin. Telefon: 0332 302 82 32, E-posta: bilgi@ozgurisg.net. İş Sağlığı ve Güvenliği hizmetleri için WhatsApp, telefon ve e-posta ile ulaşın.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'İletişim, Konya İSG, Özgür OSGB Telefon, İSG İletişim, Selçuklu OSGB');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://ozgurisg.net'}/iletisim`);
    }

    // Schema.org JSON-LD for FAQ
    const existingScript = document.querySelector('script[type="application/ld+json"][data-page="contact"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page', 'contact');
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "İSG hizmeti almak zorunlu mu?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Evet, 6331 sayılı İş Sağlığı ve Güvenliği Kanunu gereği tüm işyerleri İSG hizmeti almak zorundadır. Bu hizmet çalışan sayısına göre farklı şekillerde alınabilir."
          }
        },
        {
          "@type": "Question",
          "name": "Hangi işyerleri İSG hizmeti almalı?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "1 veya daha fazla çalışanı olan tüm işyerleri İSG hizmeti almak zorundadır. Bu kamu ve özel sektör ayrımı olmaksızın tüm işyerlerini kapsar."
          }
        },
        {
          "@type": "Question",
          "name": "İSG hizmeti maliyeti nasıl belirlenir?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "İSG hizmet maliyeti işyerindeki çalışan sayısı, risk grubu ve hizmet kapsamına göre belirlenir. Detaylı fiyat bilgisi için bizimle iletişime geçebilirsiniz."
          }
        },
        {
          "@type": "Question",
          "name": "Ne kadar sürede hizmet vermeye başlıyorsunuz?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sözleşme imzalandıktan sonra en geç 1 hafta içinde hizmet vermeye başlıyoruz. Acil durumlar için daha hızlı başlangıç da mümkündür."
          }
        }
      ]
    });
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"][data-page="contact"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (message.length > 500) {
      setSubmitStatus('error');
      setStatusMessage('Mesaj 500 karakteri geçemez.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');
    setStatusMessage('');

    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('email', email);
      formData.append('phone', phone);
      formData.append('subject', subject);
      formData.append('message', message);

      const response = await fetch('https://readdy.ai/api/form/d49qb6olr2seorfomk90', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setStatusMessage('Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.');
        setName('');
        setEmail('');
        setPhone('');
        setSubject('');
        setMessage('');
      } else {
        throw new Error('Form gönderimi başarısız');
      }
    } catch (error) {
      setSubmitStatus('error');
      setStatusMessage('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-teal-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">İletişim</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Bizimle iletişime geçin, İSG hizmetlerimiz hakkında detaylı bilgi alın.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">İletişime Geçin</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Size nasıl yardımcı olabileceğimizi öğrenmek için bizimle iletişime geçin
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Contact Form */}
            <div className="bg-gray-50 rounded-xl p-8 h-fit">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Mesaj Gönderin</h3>
              
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="ornek@email.com"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="0532 123 45 67"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Konu *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Konu seçin</option>
                      <option value="İSG Hizmeti">İSG Hizmeti</option>
                      <option value="Risk Değerlendirmesi">Risk Değerlendirmesi</option>
                      <option value="Eğitim Hizmetleri">Eğitim Hizmetleri</option>
                      <option value="Sağlık Gözetimi">Sağlık Gözetimi</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Mesaj *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                    placeholder="Mesajınızı buraya yazın... (Maksimum 500 karakter)"
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {message.length}/500
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                </button>
                
                {statusMessage && (
                  <div className={`p-4 rounded-lg ${
                    submitStatus === 'success' 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {statusMessage}
                  </div>
                )}
              </form>
            </div>
            
            {/* Map */}
            <div className="h-full min-h-[500px]">
              <div className="bg-gray-100 rounded-xl overflow-hidden h-full">
                <iframe
                  src="https://www.google.com/maps?q=37.9519758,32.5259407&hl=tr&z=18&output=embed"
                  width="100%"
                  height="500"
                  style={{border:0, minHeight: '500px'}}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Özgür OSGB - Sancak Mahallesi Konum"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Contact Info */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Diğer İletişim Yolları</h2>
            <p className="text-lg text-gray-600">
              Size en uygun iletişim yolunu seçin
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-8 text-center shadow-sm">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-whatsapp-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">WhatsApp</h3>
              <p className="text-gray-600 mb-6">
                Hızlı iletişim için WhatsApp üzerinden mesaj gönderin
              </p>
              <a 
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  window.open('https://wa.me/903323028232?text=Merhaba, İSG hizmetleriniz hakkında bilgi almak istiyorum.', '_blank');
                }}
                className="inline-flex items-center justify-center font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap bg-green-600 text-white hover:bg-green-700 px-6 py-3"
              >
                WhatsApp'tan Yaz
              </a>
            </div>

            <div className="bg-white rounded-xl p-8 text-center shadow-sm">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-phone-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Telefon</h3>
              <p className="text-gray-600 mb-6">
                Doğrudan arayarak bilgi alabilirsiniz
              </p>
              <a 
                href="tel:+903323028232"
                className="inline-flex items-center justify-center font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap bg-blue-600 text-white hover:bg-blue-700 px-6 py-3"
              >
                Hemen Ara
              </a>
            </div>

            <div className="bg-white rounded-xl p-8 text-center shadow-sm">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">E-posta</h3>
              <p className="text-gray-600 mb-6">
                Detaylı sorularınız için e-posta gönderin
              </p>
              <a 
                href="mailto:bilgi@ozgur.isg.net"
                className="inline-flex items-center justify-center font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap bg-purple-600 text-white hover:bg-purple-700 px-6 py-3"
              >
                E-posta Gönder
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Sık Sorulan Sorular</h2>
            <p className="text-lg text-gray-600">
              En çok merak edilen sorular ve cevapları
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">İSG hizmeti almak zorunlu mu?</h3>
              <p className="text-gray-600">
                Evet, 6331 sayılı İş Sağlığı ve Güvenliği Kanunu gereği tüm işyerleri İSG hizmeti almak zorundadır. 
                Bu hizmet çalışan sayısına göre farklı şekillerde alınabilir.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Hangi işyerleri İSG hizmeti almalı?</h3>
              <p className="text-gray-600">
                1 veya daha fazla çalışanı olan tüm işyerleri İSG hizmeti almak zorundadır. 
                Bu kamu ve özel sektör ayrımı olmaksızın tüm işyerlerini kapsar.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">İSG hizmeti maliyeti nasıl belirlenir?</h3>
              <p className="text-gray-600">
                İSG hizmet maliyeti işyerindeki çalışan sayısı, risk grubu ve hizmet kapsamına göre belirlenir. 
                Detaylı fiyat bilgisi için bizimle iletişime geçebilirsiniz.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Ne kadar sürede hizmet vermeye başlıyorsunuz?</h3>
              <p className="text-gray-600">
                Sözleşme imzalandıktan sonra en geç 1 hafta içinde hizmet vermeye başlıyoruz. 
                Acil durumlar için daha hızlı başlangıç da mümkündür.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IletisimPage;
